import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  initialSWXAutomateData,
  activateReferralChannelsStart,
  draftReferralChannelsStart,
  selectDraftChannels,
  selectChannelErrors
} from 'redux/channels';
import { currentUser } from 'redux/session/selectors';
import { addAlert, removeAlert } from 'redux/alerts/actions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, Loading } from 'carbon-components-react';
import { Checkmark16, Launch16 } from '@carbon/icons-react';
import { setSandBoxTestingMode } from 'redux/lodgeEnquiry';
import { APP_URL } from 'types';
import { useHistory } from 'react-router-dom';
import AdvancedFeaturesTile from '../advancedFeatures';
import PrimaryContentForm from '../primaryContent';
dayjs.extend(utc);

export const SWXAutomateComponent: React.FC<any> = ({
  advancedOptions,
  copiedAdvanceFeaturesValue,
  primaryContentOptions,
  copiedPrimaryContentOption
}) => {
  const params = new URLSearchParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const draftChannel: any = useSelector(selectDraftChannels);
  const error: any = useSelector(selectChannelErrors);
  const user: any = useSelector(currentUser);

  const [organisationId, setOrganisationId] = useState<any>();
  const [channelId, setChannelId] = useState<any>();
  const [
    isAdvancedOptionsInvavid,
    setIsAdvancedOptionsInvavid
  ] = useState<boolean>(false);

  const [
    isActivateDraftBtnDisable,
    setIsActivateDraftBtnDisable
  ] = useState<boolean>(true);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isPrimaryContentInvavid, setPrimaryContentInvalid] = useState<boolean>(
    false
  );

  const name = dayjs(new Date()).utc().local().format().toString(); //TODO

  const learnMoreHandler = () => {
    const win: Window | null = window.open(
      'https://www.smarterwx.com/smarterwx-automate',
      '_blank'
    );
    win?.focus();
  };

  useEffect(() => {
    if (copiedAdvanceFeaturesValue) {
      const values = JSON.parse(copiedAdvanceFeaturesValue);
      advancedOptions.current = {
        maxEnquiryArea: values.maxEnquiryArea
          ? values.maxEnquiryArea / 1e6
          : undefined,
        splitEnquiries: values.splitEnquiries ? 'split' : undefined,
        referralSummaryFrequency: values.referralSummaryFrequency,
        referralSummaryEmail: values.referralSummaryEmail,
        isExpandPanel: true
      };
    }
  }, [copiedAdvanceFeaturesValue, advancedOptions]);

  useEffect(() => {
    if (copiedPrimaryContentOption) {
      primaryContentOptions.current = {
        filenameOption: copiedPrimaryContentOption.filenameOption,
        filenameValue: copiedPrimaryContentOption.filenameValue,
        webMap: copiedPrimaryContentOption.webMap,
        responseType: copiedPrimaryContentOption.responseType
      };
    }
  }, [copiedPrimaryContentOption, primaryContentOptions]);

  const addAdvancedOptions = (payload: any) => {
    if (advancedOptions?.current) {
      payload.maxEnquiryArea =
        advancedOptions.current?.maxEnquiryArea &&
        advancedOptions.current?.splitEnquiries
          ? advancedOptions.current.maxEnquiryArea * 1e6
          : undefined;
      payload.referralSummaryFrequency =
        advancedOptions.current?.referralSummaryFrequency;
      payload.referralSummaryEmail =
        advancedOptions.current?.referralSummaryEmail &&
        advancedOptions.current?.referralSummaryFrequency !== 'NEVER'
          ? advancedOptions.current.referralSummaryEmail.split(',').join(';')
          : undefined;
    }

    payload.primaryContent = {
      filenameOption: primaryContentOptions.current?.filenameOption,
      filenameValue: primaryContentOptions.current?.filenameValue,
      webMap: primaryContentOptions.current?.webMap,
      responseType: primaryContentOptions.current?.responseType
    };

    return payload;
  };

  useEffect(() => {
    setOrganisationId(user?.organisationId || user?.id);
  }, [user]);

  useEffect(() => {
    const error_id = 'channel_1';
    if (error) {
      const notificationProps = {
        title: '',
        subtitle: `A SmarterWX Automate account was not found for your organisation`,
        lowContrast: true,
        doActions: learnMoreHandler,
        actionButtonText: 'Learn more'
      };

      dispatch(
        addAlert('DELETE_USER_GROUP', 'error', '', '', {
          id: error_id,
          withClose: false,
          type: 'inline',
          notificationProps
        })
      );
    }
    return () => {
      dispatch(removeAlert(error_id));
    };
  }, [dispatch, error]);

  useEffect(() => {
    if (draftChannel) {
      const { submitting, id } = draftChannel;
      setIsVerifying(submitting);
      setChannelId(id);
      setIsActivateDraftBtnDisable(
        draftChannel && !draftChannel?.SmarterWXAutomateConfiguration
      );
    }
  }, [draftChannel, isVerifying]);

  const onSaveDraftHandler = () => {
    const packedDraftPayload: any = {
      ...initialSWXAutomateData,
      name,
      status: 'DRAFT'
    };

    draftChannel?.WebhookConfiguration !== null && channelId
      ? dispatch(
          activateReferralChannelsStart({
            organisationId,
            channelId,
            requestBody: addAdvancedOptions(packedDraftPayload)
          })
        )
      : dispatch(
          draftReferralChannelsStart({
            organisationId,
            requestBody: addAdvancedOptions(packedDraftPayload)
          })
        );
  };

  const onMakeActiveHandler = () => {
    const packedActivePayload: any = {
      ...initialSWXAutomateData,
      status: 'LIVE'
    };

    if (organisationId && channelId) {
      dispatch(
        activateReferralChannelsStart({
          organisationId,
          channelId,
          requestBody: addAdvancedOptions(packedActivePayload)
        })
      );
    }
  };

  const onVerifyingHandler = () => {
    if (primaryContentOptions?.current) {
      if (
        primaryContentOptions.current.filenameOption !== '' &&
        primaryContentOptions.current.filenameValue === ''
      ) {
        return setPrimaryContentInvalid(true);
      }
    }
    const payload = {
      ...initialSWXAutomateData,
      name,
      SmarterWXAutomateConfiguration: {
        test: true
      }
    };

    dispatch(
      draftReferralChannelsStart({
        organisationId,
        requestBody: addAdvancedOptions(payload)
      })
    );
  };

  const sandBoxTestHandler = () => {
    dispatch(setSandBoxTestingMode({ isSandboxTest: true }));
    params.append('isSandboxTest', 'true');
    history.push({
      pathname: APP_URL.LODGE_NEW_ENQUIRY,
      search: '?' + params.toString()
    });
  };

  return (
    <div className="swx-automate--wrapper">
      <PrimaryContentForm
        primaryContentOptions={primaryContentOptions}
        setPrimaryContentInvalid={(flag: boolean) =>
          setPrimaryContentInvalid(flag)
        }
      />
      <AdvancedFeaturesTile
        advancedOptions={advancedOptions}
        setAdvancedOptionsInvavid={(flag: boolean) =>
          setIsAdvancedOptionsInvavid(flag)
        }
      />
      <hr className="section__divider" />
      <div className="form-button--wrapper">
        <Button
          className={
            draftChannel?.swxAutomateVerified
              ? 'swx-automate-btn--verified'
              : ''
          }
          kind="tertiary"
          size="field"
          onClick={() =>
            !(draftChannel && draftChannel?.swxAutomateVerified)
              ? onVerifyingHandler()
              : {}
          }
          disabled={error !== '' || isVerifying}
        >
          {draftChannel?.swxAutomateVerified ? 'Verified' : 'Verify'}
          {isVerifying && !draftChannel?.swxAutomateVerified ? (
            <Loading
              small
              description="Active loading indicator"
              withOverlay={false}
            />
          ) : draftChannel?.swxAutomateVerified ? (
            <Checkmark16 className="tick--icon" />
          ) : (
            ''
          )}
        </Button>
        <Button
          className="reg-submit"
          type="submit"
          kind={'secondary'}
          data-testid="submit"
          size="field"
          onClick={() => onSaveDraftHandler()}
          disabled={
            !(draftChannel && draftChannel?.swxAutomateVerified) ||
            isVerifying ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
        >
          {'Save draft'}
        </Button>
        <Button
          disabled={
            isActivateDraftBtnDisable ||
            isVerifying ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
          renderIcon={Launch16}
          kind="tertiary"
          size="field"
          onClick={() => sandBoxTestHandler()}
        >
          Test run
        </Button>
        <Button
          className="submit-active"
          type="submit"
          kind={'primary'}
          size="field"
          data-testid="submit-active"
          onClick={() => onMakeActiveHandler()}
          disabled={
            isActivateDraftBtnDisable ||
            isVerifying ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
        >
          {'Make Active'}
        </Button>
        {/* {isVerifying && (
        <Loading
          small
          description="Active loading indicator"
          withOverlay={false}
        />
      )} */}
      </div>
      <p className="footer--content">
        Save your draft and use ‘Test run’ to lodge a mock enquiry in Sandbox
        testing mode
      </p>
    </div>
  );
};
export default SWXAutomateComponent;
